import { Component, lazy, memo, Suspense } from "react";
import type { ComponentType, ReactNode } from "react";
import PageSkel, { type PageKey } from "./components/PageSkel";

/**
 * The console's pages as downloads of their own (owner 10/11: 「确保流畅画面加载
 * 快没有bug」). 对话 is what CS open first and comes with the console; every other
 * page is fetched when opened, or in the quiet moments after sign-in (prefetch),
 * so a tab still opens at once.
 *
 * A build names its files by content and a deploy replaces them: a console
 * opened before a deploy asks, at its next tab, for a file that is gone, nginx
 * answers 404, and without a catch React takes the whole page down. The shop
 * met it at 购买 on 2026-10-02 (lazior-landing src/shop/lib/newBuild.ts); this
 * is the same answer. A failed load reloads the page into the new build, once:
 * a second failure within a minute is an outage, not a deploy, and gets one
 * line with 刷新 in place of the page — never a blank, never a loop.
 * scripts/check-lazy.mjs fails the build on a load that does not come through here.
 */

const RELOADED_AT = "lazior_view_reload_at";
/** A second failure this soon after the reload is not a deploy. */
const ONCE_MS = 60_000;

/*
 * A page's styles are a file of their own, put in <head> by the import that fetches the page. When that file failed
 * (a phone's network dropping for a moment), the page still came: Vite never asks for a file twice, so the next
 * opening drew 小爱 as bare words, for good — and one still on its way drew it so until it came (QA 2026-10-24
 * C10, met in the comparison runs). So a page is shown only once every stylesheet of ours is in: one still coming is
 * waited for, a failed one is asked for again, and one that fails twice is a failed load like any other.
 */
const failedSheets = new WeakSet<HTMLLinkElement>();
document.addEventListener(
  "error",
  (e) => {
    if (e.target instanceof HTMLLinkElement && e.target.rel === "stylesheet") failedSheets.add(e.target);
  },
  true,
);
/**
 * A failed one: Chrome gives it an empty sheet all the same, so `sheet` alone does not tell (none of ours is empty).
 * A crossorigin sheet whose connection was refused throws on `cssRules` instead: failed too.
 */
const sheetFailed = (link: HTMLLinkElement) => {
  if (failedSheets.has(link)) return true;
  try {
    return !!link.sheet && link.sheet.cssRules.length === 0;
  } catch {
    return true;
  }
};
/** Whether a stylesheet came; a slow one is let go after a while (the page then shows, and its looks follow). */
function sheetIn(link: HTMLLinkElement): Promise<boolean> {
  if (sheetFailed(link)) return Promise.resolve(false);
  if (link.sheet) return Promise.resolve(true);
  return new Promise((done) => {
    const end = () => {
      window.clearTimeout(slow);
      link.removeEventListener("load", end);
      link.removeEventListener("error", end);
      done(!sheetFailed(link));
    };
    const slow = window.setTimeout(end, 15_000);
    link.addEventListener("load", end);
    link.addEventListener("error", end);
  });
}
async function stylesIn(): Promise<void> {
  const ours = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')].filter(
    (l) => new URL(l.href, location.href).origin === location.origin && (!l.sheet || sheetFailed(l)),
  );
  await Promise.all(
    ours.map(async (link) => {
      if (await sheetIn(link)) return;
      const again = document.createElement("link");
      again.rel = "stylesheet";
      again.href = link.href;
      if (link.crossOrigin != null) again.crossOrigin = link.crossOrigin;
      link.replaceWith(again);
      if (!(await sheetIn(again))) throw new Error(`stylesheet failed twice: ${link.href}`);
    }),
  );
}

/** A loader that, when this page is older than the build deployed, reloads into that build once. */
export function orReload<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load()
      .then(async (page) => {
        await stylesIn();
        return page;
      })
      .catch((err: unknown) => {
        try {
          if (Date.now() - Number(sessionStorage.getItem(RELOADED_AT) || 0) > ONCE_MS) {
            sessionStorage.setItem(RELOADED_AT, String(Date.now()));
            window.location.reload();
            // The loading line stays until the new page replaces this one.
            return new Promise<T>(() => undefined);
          }
        } catch {
          /* no storage, so no telling a first failure from a tenth: the 刷新 line, never a loop */
        }
        throw err;
      });
}

const MEMO = Symbol.for("react.memo");

/**
 * A page fetched when first shown, or ahead (its `preload`, which prefetch() is handed). Drawn again only when what App
 * hands it changes: 今天, 跟进 and 话术榜 were plain components, and every render of App — a number beside 对话, 小爱's
 * line, a press on the menu — drew each of them again whole, on screen or not.
 *
 * Fetched ahead, it is drawn in the very render that first shows it. React's lazy() always waits once, a promise never
 * being answered on the spot, so a page whose file had long arrived still put up its placeholder, turned its menu item
 * and ran the line for a moment at its first opening — 0.1–0.3 s on 客户 (10/27). Handed what came, it does not wait.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy's own bound
export function lazyView<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  const once = orReload(load);
  const memoed = (m: { default: T }) =>
    (m.default as unknown as { $$typeof?: symbol }).$$typeof === MEMO ? m : { default: memo(m.default) as unknown as T };
  let ready: { default: T } | null = null;
  const View = lazy(() => {
    const got = ready;
    // A thenable that answers at once: lazy() takes the page in this same render.
    if (got) return { then: (done: (m: { default: T }) => void) => done(got) } as unknown as Promise<{ default: T }>;
    return once().then(memoed);
  });
  /** Fetched in a quiet moment: never reloads (a failure is asked for again, through orReload, on opening). */
  const preload = () =>
    load().then(async (m) => {
      await stylesIn();
      ready ??= memoed(m);
    });
  return Object.assign(View, { preload });
}

const idle = () =>
  new Promise<void>((done) => {
    const w = window as Window & { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(() => done(), { timeout: 3000 });
    else window.setTimeout(done, 300);
  });

/**
 * Fetches pages one after another whenever the browser has nothing else to
 * do, so opening one later is instant; stops when the returned function is
 * called. A fetch that fails here never reloads anything: the page is asked
 * for again, through orReload, when somebody opens it.
 */
export function prefetch(loaders: Array<() => Promise<unknown>>): () => void {
  let stopped = false;
  void (async () => {
    for (const load of loaders) {
      await idle();
      if (stopped) return;
      try {
        await load();
      } catch {
        /* opened later, it is asked for again */
      }
    }
  })();
  return () => {
    stopped = true;
  };
}

/** What a page shows while its file arrives; it fades in only after a moment, so a fetched page never flickers it. */
function Loading() {
  return <p className="view-loading">在载入…</p>;
}

/** A page that could not be loaded or drawn: one line and 刷新; the rest of the console goes on. */
class PageBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(err: unknown) {
    console.error("page failed", err);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="view-failed" role="alert">
        <p>这一页打不开，可能刚更新过</p>
        <button type="button" onClick={() => window.location.reload()}>
          刷新
        </button>
      </div>
    );
  }
}

/**
 * One lazily loaded page: its own failure, apart from the others, and while
 * its file is on the way, its placeholder (PageSkel) — the same one it shows
 * while its first read is out, so the two waits look like one. A part of a
 * page (素材库 on 小爱) passes its own as `wait`: without it, the moment its
 * file took showed the old 在载入… (QA 2026-10-22).
 */
export function ViewShell({ children, page, wait }: { children: ReactNode; page?: PageKey; wait?: ReactNode }) {
  return (
    <PageBoundary>
      <Suspense fallback={wait ?? (page ? <PageSkel page={page} /> : <Loading />)}>{children}</Suspense>
    </PageBoundary>
  );
}
